"use client"

import * as React from 'react'
import { XIcon } from 'lucide-react'
import { Dialog as SheetPrimitive } from 'radix-ui'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useTranslations } from "next-intl"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-foreground/30 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = 'right',
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left'
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'fixed z-50 flex flex-col gap-6 bg-background transition ease-in-out shadow-[0_0_20px_0_rgba(0,0,0,0.1)] data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500',
          /*
            Padding belongs to the SIDE, not to every sheet.

            `p-12 lg:p-24 !pb-12` was on the base, which is right for a full-height side sheet
            and wrong for one that opens from an edge and is only as tall as its contents — on a
            bottom sheet holding a single row of cards it was most of the drawer.

            It also could not be overridden. `!pb-12` and a caller's `!pb-6` are both `!important`
            and equally specific, so the winner is whichever Tailwind emits later — and it sorts
            by scale, so the larger value always won no matter what the call site asked for.
            Scoping it here means a caller can simply say what it wants.
          */
          (side === 'left' || side === 'right') && 'p-12 lg:p-24 !pb-12',
          /*
            Vertical only. An edge sheet is full-bleed horizontally and its children carry their
            own gutter — the carousel inside this one measures that gutter to break its track out
            to the viewport, so a padding here as well would both double the inset and lie to it.
          */
          (side === 'top' || side === 'bottom') && 'py-6 lg:py-8',
          side === 'right' &&
            'inset-y-0 right-0 h-full min-w-1/2 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
          side === 'left' &&
            'inset-y-0 left-0 h-full min-w-1/2 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
          side === 'top' &&
            'inset-x-0 top-0 h-auto data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
          side === 'bottom' &&
            'inset-x-0 bottom-0 h-auto data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          className
        )}
        {...props}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="sheet-header" className={cn('flex flex-col gap-6', className)} {...props} />
  )
}

function SheetFooter({ className, children, ...props }: React.ComponentProps<'div'>) {
  const t = useTranslations("ui.sheet")
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-row items-center justify-end gap-2 pt-6 border-t', className)}
      {...props}
    >
      {children}
      <SheetPrimitive.Close asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          {/* <XIcon className="size-3.5" strokeWidth={2.25} /> */}
          {t("close")}
        </Button>
      </SheetPrimitive.Close>
    </div>
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-bold text-foreground text-2xl md:text-4xl', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-base text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}